import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { StatCard } from './components/StatCard';
import { SectionStatusChip } from './components/SectionStatusChip';
import { KpiTimeChart } from './components/KpiTimeChart';
import { SectionExportButton } from './components/SectionExportButton';
import { GlosarioHelpButton } from './components/GlosarioHelpButton';
import { fmtNum, fmtNumShort, fmtMoney, fmtMoneyShort, fmtPeriodo, CHART_COLORS } from './components/kpiUtils';
import PrestamoGananciaModal from './components/PrestamoGananciaModal';
import ComposicionMesModal from './components/ComposicionMesModal';
import { Banknote, Upload, CheckCircle2, AlertTriangle, TrendingUp, Percent, DollarSign, Eye, RefreshCw, Loader2 } from 'lucide-react';

const ESTADO_COLORS = {
  'Rechazado': '#f43f5e', 'Vigente': '#10b981', 'Mora': '#f59e0b',
  'Cancelado': '#94a3b8', 'Solicitado': '#0ea5e9', 'Aprobado': '#22c55e',
};

const KpisPrestamos = () => {
  const [data, setData] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [selPrestamo, setSelPrestamo] = useState(null);
  const [composMes, setComposMes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [scraping, setScraping] = useState(false);
  const fileRef = useRef(null);
  const rootRef = useRef(null);
  const pollRef = useRef(null);

  const load = async () => {
    try { const r = await api.get('/kpis/prestamos'); setData(r.data); }
    catch (e) { toast.error('Error al cargar Préstamos'); }
    finally { setLoading(false); }
    try { const rd = await api.get('/kpis/prestamos/detalle'); setDetalle(rd.data); return rd.data; }
    catch (e) { /* detalle opcional */ }
  };
  useEffect(() => { load(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);

  // Reanuda el polling si el scrape ya venía corriendo
  useEffect(() => {
    if (detalle?.scrape?.status === 'en_ejecucion' && !pollRef.current) startPoll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detalle?.scrape?.status]);

  const startPoll = () => {
    setScraping(true);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const rd = await api.get('/kpis/prestamos/detalle');
        setDetalle(rd.data);
        const st = rd.data?.scrape?.status;
        if (st !== 'en_ejecucion') {
          clearInterval(pollRef.current); pollRef.current = null; setScraping(false);
          if (st === 'ok') toast.success('Cuotas y mora actualizadas desde Waya.');
          else if (st === 'error') toast.error(rd.data?.scrape?.last_error || 'La actualización de cuotas falló.');
        }
      } catch (e) { /* reintenta en el próximo tick */ }
    }, 6000);
  };

  const actualizarDetalle = async () => {
    setScraping(true);
    try {
      await api.post('/kpis/automatizaciones/prestamos-detalle-mora/ejecutar');
      toast.info('Actualizando cuotas y mora desde Waya… puede tardar varios minutos.');
      startPoll();
    } catch (e) {
      setScraping(false);
      toast.error(e.response?.data?.detail || 'No se pudo iniciar la actualización.');
    }
  };

  const subir = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/kpis/prestamos/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${r.data.total} préstamos importados`); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo importar'); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const k = data?.kpis || {};
  const puede_cargar = data?.puede_cargar;
  const CANON = ['solicitado', 'rechazado', 'aprobado', 'vigente', 'mora', 'cancelado', 'otro', 'sin dato'];
  const estados = useMemo(() => {
    const present = data?.estados || [];
    const canon = present.filter((e) => CANON.includes(String(e).trim().toLowerCase()));
    return (canon.length ? canon : present).slice(0, 8);
  }, [data]);
  const mesData = useMemo(() => (data?.por_mes || []).map((m) => {
    const row = { label: fmtPeriodo(m.periodo, true) };
    estados.forEach((e) => { row[e] = m[e] || 0; });
    return row;
  }), [data, estados]);
  const estadoSeries = estados.map((e, i) => ({ key: e, name: e, color: ESTADO_COLORS[e] || CHART_COLORS[i % CHART_COLORS.length] }));

  if (loading) return <div className="p-8 text-slate-500" data-testid="kpis-prestamos-loading">Cargando préstamos…</div>;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" ref={rootRef} data-testid="kpis-prestamos">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-emerald-500/10"><Banknote className="h-6 w-6 text-emerald-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Préstamos</h1>
            <div className="mt-1.5"><SectionStatusChip seccionKey="kpis-prestamos" /></div>
            <p className="text-sm text-slate-500">Solicitudes, aprobación, mora y monto otorgado</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <GlosarioHelpButton seccion="prestamos" testId="prestamos-help-btn" />
          <SectionExportButton title="Préstamos" targetRef={rootRef} testId="prestamos-export-btn" />
          {puede_cargar && (
            <Button onClick={actualizarDetalle} disabled={scraping} variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50 active:scale-95 transition-transform" data-testid="prestamos-actualizar-cuotas-btn">
              {scraping ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              {scraping ? 'Actualizando cuotas…' : 'Actualizar cuotas y mora'}
            </Button>
          )}
          {puede_cargar && (
            <>
              <Button onClick={() => fileRef.current?.click()} disabled={importing} className="bg-emerald-600 hover:bg-emerald-700 active:scale-95 transition-transform" data-testid="prestamos-subir-btn">
                <Upload className="h-4 w-4 mr-1" /> {importing ? 'Importando…' : 'Subir reporte (XLSX)'}
              </Button>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={subir} data-testid="prestamos-file-input" />
            </>
          )}
        </div>
      </div>

      {!data?.cargado ? (
        <Card className="p-10 text-center border-dashed border-2 border-slate-200 dark:border-slate-700" data-testid="prestamos-empty">
          <Banknote className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Todavía no hay un reporte de préstamos cargado.</p>
          <p className="text-sm text-slate-400 mt-1">{puede_cargar ? 'Subí el Excel "Préstamos por Estado".' : 'Pedile a un administrador que cargue el reporte.'}</p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <StatCard label="Solicitudes" value={fmtNum(k.total)} icon={Banknote} accent="#4f46e5" testId="prestamos-kpi-total" />
            <StatCard label="Tasa aprobación" value={`${k.tasa_aprobacion ?? 0}%`} icon={CheckCircle2} accent="#10b981" deltaLabel={`${fmtNum(k.aprobados)} aprobados`} testId="prestamos-kpi-aprob" />
            <StatCard label="En mora" value={fmtNum(k.en_mora)} icon={AlertTriangle} accent="#f59e0b" deltaLabel={`${k.tasa_mora ?? 0}% de otorgados`} testId="prestamos-kpi-mora" />
            <StatCard label="Vigentes" value={fmtNum(k.vigentes)} icon={TrendingUp} accent="#0ea5e9" testId="prestamos-kpi-vigentes" />
            <StatCard label="Monto otorgado" value={fmtMoney(k.monto_otorgado)} icon={Banknote} accent="#7c3aed" testId="prestamos-kpi-monto" />
            <StatCard label="Préstamo promedio" value={fmtMoney(k.prestamo_promedio)} icon={Percent} accent="#0ea5e9" deltaLabel="monto más frecuente" testId="prestamos-kpi-promedio" />
          </div>

          {detalle && (detalle.prestamos || []).length > 0 && (
            <Card className="p-0 overflow-hidden mb-6 border-emerald-200" data-testid="prestamos-ganancia-section">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 p-5 border-b border-emerald-200">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2.5 rounded-full bg-emerald-600 shrink-0"><DollarSign className="text-white" size={20} /></div>
                    <div>
                      <h2 className="text-base font-semibold text-emerald-900">Ganancia de préstamos (realizada)</h2>
                      <p className="text-xs text-emerald-700/70 mt-0.5">Lo efectivamente ganado: cuotas cerradas (total − capital) + punitorios cobrados. {detalle.resumen.n_con_pagos}/{detalle.resumen.n_prestamos} préstamos con cobros.</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[11px] uppercase tracking-wide text-emerald-700/70 font-medium">Total realizado</p>
                    <p className="text-2xl font-bold font-mono text-emerald-700 tabular-nums" data-testid="prestamos-ganancia-total">{fmtMoney(detalle.resumen.total_realizado)}</p>
                    <p className="text-xs text-emerald-800 mt-0.5">{detalle.resumen.pct_global}% de {fmtMoney(detalle.resumen.total_esperado)} esperados</p>
                  </div>
                </div>
              </div>
              {(detalle.serie_mensual || []).some((s) => s.ingreso > 0) && (
                <div className="p-5 border-b border-slate-100" data-testid="prestamos-ganancia-serie">
                  <h3 className="text-sm font-medium text-slate-600 mb-3">Ganancia realizada por mes <span className="text-slate-400 font-normal">(últimos 12 meses · según fecha de pago)</span></h3>
                  <KpiTimeChart data={(detalle.serie_mensual || []).map((s) => ({ label: fmtPeriodo(s.mes, true), ingreso: s.ingreso }))}
                    series={[{ key: 'ingreso', name: 'Ganancia realizada', color: '#10b981' }]} type="barras" valueFormatter={fmtMoney} axisFormatter={fmtMoneyShort} height={260} showLegend={false} testId="prestamos-ganancia-serie-chart" />
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-xs text-slate-500 mb-2">Elegí un mes para ver cómo se compone (quién pagó y cuánto):</p>
                    <div className="flex flex-wrap gap-1.5" data-testid="prestamos-compos-selector">
                      {(detalle.serie_mensual || []).map((s) => (
                        <button key={s.mes} type="button" disabled={!(s.ingreso > 0)}
                          onClick={() => setComposMes(s.mes)}
                          data-testid={`prestamos-compos-mes-${s.mes}`}
                          className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${s.ingreso > 0 ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-600 hover:text-white active:scale-95' : 'bg-slate-50 text-slate-300 cursor-not-allowed'}`}>
                          {fmtPeriodo(s.mes, true)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-50 text-slate-500">
                    <th className="text-left font-medium px-4 py-2.5">Préstamo</th>
                    <th className="text-left font-medium px-4 py-2.5">CUIT</th>
                    <th className="text-right font-medium px-4 py-2.5">Capital</th>
                    <th className="text-right font-medium px-4 py-2.5">Ganancia realizada</th>
                    <th className="text-right font-medium px-4 py-2.5">% realizado</th>
                    <th className="text-right font-medium px-4 py-2.5">Mora</th>
                    <th className="text-right font-medium px-4 py-2.5"></th>
                  </tr></thead>
                  <tbody>
                    {detalle.prestamos.map((p) => (
                      <tr key={p.prestamo_id} className={`border-t border-slate-100 ${p.en_mora ? 'bg-rose-50/40' : ''}`} data-testid={`prestamo-fila-${p.prestamo_id}`}>
                        <td className="px-4 py-2.5">
                          <span className="text-slate-700">{p.nombre}</span>
                          <span className="text-xs text-slate-400 ml-1">#{p.prestamo_id}</span>
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{p.cuit}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{fmtMoney(p.monto_solicitado)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-emerald-700">{fmtMoney(p.ingreso_realizado)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{p.pct_realizado}%</td>
                        <td className="px-4 py-2.5 text-right tabular-nums" data-testid={`prestamo-mora-${p.prestamo_id}`}>
                          {p.en_mora
                            ? <span className="text-rose-600 font-semibold">{fmtMoney(p.total_mora)}<span className="block text-[10px] text-rose-400 font-normal">{p.dias_mora} días</span></span>
                            : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <Button size="sm" variant="outline" onClick={() => setSelPrestamo(p.prestamo_id)} data-testid={`prestamo-ver-${p.prestamo_id}`}>
                            <Eye size={14} className="mr-1.5" /> Ver detalle
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {detalle && detalle.resumen && (detalle.prestamos || []).length > 0 && (
            <Card className="p-0 overflow-hidden mb-6 border-rose-200" data-testid="prestamos-mora-consolidada">
              <div className="bg-gradient-to-br from-rose-50 to-rose-100/50 p-5 border-b border-rose-200">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2.5 rounded-full bg-rose-600 shrink-0"><AlertTriangle className="text-white" size={20} /></div>
                    <div>
                      <h2 className="text-base font-semibold text-rose-900">Mora consolidada</h2>
                      <p className="text-xs text-rose-700/70 mt-0.5">Deuda vencida calculada sobre el detalle de cuotas. {detalle.resumen.prestamos_con_mora}/{detalle.resumen.n_prestamos} préstamos en mora ({detalle.resumen.tasa_mora}%).</p>
                      {detalle.scrape?.updated_at && (
                        <p className="text-[11px] text-rose-500/70 mt-0.5" data-testid="prestamos-mora-actualizado">
                          Detalle de cuotas actualizado: {new Date(detalle.scrape.updated_at).toLocaleString('es-AR')}{detalle.scrape.rango ? ` · rango ${detalle.scrape.rango}` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[11px] uppercase tracking-wide text-rose-700/70 font-medium">Total en mora</p>
                    <p className="text-2xl font-bold font-mono text-rose-700 tabular-nums" data-testid="prestamos-mora-total">{fmtMoney(detalle.resumen.total_mora)}</p>
                    <p className="text-xs text-rose-800 mt-0.5">Capital {fmtMoney(detalle.resumen.capital_mora)} · Punitorios {fmtMoney(detalle.resumen.punitorios_saldo)}</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-5 border-b border-slate-100">
                <StatCard label="Total en mora" value={fmtMoney(detalle.resumen.total_mora)} icon={AlertTriangle} accent="#e11d48" testId="prestamos-mora-kpi-total" />
                <StatCard label="Capital en mora" value={fmtMoney(detalle.resumen.capital_mora)} icon={Banknote} accent="#f43f5e" testId="prestamos-mora-kpi-capital" />
                <StatCard label="Punitorios (saldo)" value={fmtMoney(detalle.resumen.punitorios_saldo)} icon={Percent} accent="#fb7185" testId="prestamos-mora-kpi-punitorios" />
                <StatCard label="Recupero esperado" value={fmtMoney(detalle.resumen.recupero_esperado)} icon={TrendingUp} accent="#10b981" testId="prestamos-mora-kpi-recupero" />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="prestamos-mora-frecuencia-tabla">
                  <thead><tr className="bg-slate-50 text-slate-500">
                    <th className="text-left font-medium px-4 py-2.5">Frecuencia</th>
                    <th className="text-right font-medium px-4 py-2.5">Préstamos</th>
                    <th className="text-right font-medium px-4 py-2.5">En mora</th>
                    <th className="text-right font-medium px-4 py-2.5">Tasa mora</th>
                    <th className="text-right font-medium px-4 py-2.5">Total mora</th>
                    <th className="text-right font-medium px-4 py-2.5">Recupero esperado</th>
                  </tr></thead>
                  <tbody>
                    {(detalle.por_frecuencia || []).map((f) => (
                      <tr key={f.frecuencia} className="border-t border-slate-100" data-testid={`mora-frec-${f.frecuencia}`}>
                        <td className="px-4 py-2.5 text-slate-700">{f.frecuencia}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{fmtNum(f.count)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-rose-600 font-medium">{fmtNum(f.prestamos_con_mora)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{f.tasa_mora}%</td>
                        <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-rose-700">{fmtMoney(f.total_mora)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{fmtMoney(f.recupero_esperado)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card className="p-5 lg:p-6" data-testid="prestamos-por-mes">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Solicitudes por mes y estado</h2>
              <KpiTimeChart data={mesData} series={estadoSeries} type="barras" stacked valueFormatter={fmtNum} axisFormatter={fmtNumShort} height={300} testId="prestamos-mes-chart" />
            </Card>
            <Card className="p-5 lg:p-6" data-testid="prestamos-monto-mes">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Monto otorgado por mes</h2>
              <KpiTimeChart data={(data?.monto_por_mes || []).map((m) => ({ label: fmtPeriodo(m.periodo, true), monto: m.monto }))}
                series={[{ key: 'monto', name: 'Otorgado', color: '#7c3aed' }]} type="area" valueFormatter={fmtMoney} axisFormatter={fmtMoneyShort} height={300} showLegend={false} testId="prestamos-monto-chart" />
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card className="p-5 lg:p-6" data-testid="prestamos-por-rango">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Distribución por rango de monto (otorgados)</h2>
              <KpiTimeChart data={(data?.por_rango || []).map((r) => ({ label: r.rango, total: r.total }))} xKey="label"
                series={[{ key: 'total', name: 'Préstamos', color: '#059669' }]} type="barras" barLayout="vertical" cellColors={CHART_COLORS}
                height={Math.max(200, (data?.por_rango || []).length * 40)} valueFormatter={fmtNum} axisFormatter={fmtNumShort} showLegend={false} testId="prestamos-rango-chart" />
            </Card>
            <Card className="p-5 lg:p-6" data-testid="prestamos-por-frecuencia">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Por frecuencia de pago</h2>
              <KpiTimeChart data={(data?.por_frecuencia || []).map((f) => ({ label: f.frecuencia, total: f.total }))} xKey="label"
                series={[{ key: 'total', name: 'Préstamos', color: '#10b981' }]} type="barras" barLayout="vertical" cellColors={CHART_COLORS}
                height={Math.max(180, (data?.por_frecuencia || []).length * 44)} valueFormatter={fmtNum} axisFormatter={fmtNumShort} showLegend={false} testId="prestamos-frecuencia-chart" />
            </Card>
          </div>

          <Card className="p-5 lg:p-6 mb-6" data-testid="prestamos-por-estado">
            <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Por estado</h2>
            <KpiTimeChart data={(data?.por_estado || []).map((e) => ({ label: e.estado, total: e.total }))} xKey="label"
              series={[{ key: 'total', name: 'Préstamos', color: '#4f46e5' }]} type="barras" barLayout="vertical" cellColors={(data?.por_estado || []).map((e) => ESTADO_COLORS[e.estado] || '#94a3b8')}
              height={Math.max(160, (data?.por_estado || []).length * 40)} valueFormatter={fmtNum} axisFormatter={fmtNumShort} showLegend={false} testId="prestamos-estado-chart" />
          </Card>

          <Card className="p-0 overflow-hidden" data-testid="prestamos-mora-tabla">
            <div className="p-5 border-b border-slate-100 dark:border-slate-800">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">Préstamos en mora ({fmtNum(k.en_mora)})</h2>
              <p className="text-xs text-slate-400 mt-1">Seguimiento de riesgo crediticio.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 dark:bg-slate-800 text-slate-500">
                  <th className="text-left font-medium px-4 py-2.5">Fecha</th>
                  <th className="text-left font-medium px-4 py-2.5">CUIT</th>
                  <th className="text-left font-medium px-4 py-2.5">Nombre</th>
                  <th className="text-right font-medium px-4 py-2.5">Monto</th>
                  <th className="text-left font-medium px-4 py-2.5">Frecuencia</th>
                </tr></thead>
                <tbody>
                  {(data?.mora || []).map((r, i) => (
                    <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="px-4 py-2.5 whitespace-nowrap text-slate-500">{r.fecha || '—'}</td>
                      <td className="px-4 py-2.5 font-mono text-xs">{r.cuit || '—'}</td>
                      <td className="px-4 py-2.5">{r.nombre || '—'}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-medium">{fmtMoney(r.monto_final)}</td>
                      <td className="px-4 py-2.5">{r.frecuencia || '—'}</td>
                    </tr>
                  ))}
                  {(data?.mora || []).length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Sin préstamos en mora. 🎉</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
          <p className="text-xs text-slate-400 mt-4">Fuente: {data?.archivo || 'Préstamos por Estado'} · cargado {data?.fecha_carga ? new Date(data.fecha_carga).toLocaleString('es-AR') : '—'}</p>
        </>
      )}
      <PrestamoGananciaModal prestamoId={selPrestamo} open={!!selPrestamo} onClose={() => setSelPrestamo(null)} />
      <ComposicionMesModal mes={composMes} open={!!composMes} onClose={() => setComposMes(null)}
        onVerPrestamo={(id) => { setComposMes(null); setSelPrestamo(id); }} />
    </div>
  );
};

export default KpisPrestamos;
